import { getQueryArgs } from "@js/nwcc-navbar.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { renderGrowl } from "@js/nwcc-growl.js";
import { resizePlot } from "@js/nwcc-plotting.js";
import * as data from "./forecast-evolution-data.js";
import * as utils from "./forecast-evolution-utils.js";
import * as plotting from "./forecast-evolution-plotly-template.js";
import { extendAddDays, extendAddMonths, extendAddSemimonths } from "@js/nwcc-extensions.js";

// Window and plotly plot resizing
window.onresize = resizePlot;

//add necessary date extensions
extendAddDays();
extendAddMonths();
extendAddSemimonths();

/**
 * Returns the list of arguments used by the <arg-details> element
 */
function getPageArgs() {
    return [
        {
            argName: "state",
            argDesc: '2-character state abbreviation. Sets the selected state in the "Select State" drop-down menu.',
            argExample: "MT",
        },
        {
            argName: "triplet",
            argDesc: "NWCC forecast station triplet. Must be a triplet that is associated with the selected state.",
            argExample: "05016000:MT:USGS",
        },
        {
            argName: "period",
            argDesc: "Forecast period. All capital letters and no spaces.",
            argExample: "APR-JUL",
        },
        {
            argName: "year",
            argDesc: "Forecast year to display.",
            argExample: "2024",
        },
        {
            argName: "forecastStatus",
            argDesc: "Comma separated list of forecast statuses to display.",
            argExample: "raw,prelim,final",
            argOptions: ["final", "prelim", "raw", "guidance", "all"],
            argInternal: true,
        },
    ];
}

/**
 * Runs at page load. This function:
 *  - Sets up the <arg-details> element
 *  - fills in the forms with any provided URL parameters
 *  - Adds event listeners to the drop down menus and buttons
 *  - Creates a plot (either empty or with data)
 *  - removes the splash screen
 */
export async function pageLoad() {
    setArgDetailsData(getPageArgs());

    const { stateSelect, pointSelect, periodSelect, yearSelect } = getFormObjects();

    // Get and query args
    const queryArgs = getQueryArgs(); // E.g. .../forecast-evolution/?state=MT&triplet=05017500:MT:USGS

    let validArgCount = 0;

    // Fill initial form values
    if (queryArgs["state"]) {
        if ([...stateSelect.options].map((o) => o.value).includes(queryArgs["state"])) {
            stateSelect.value = queryArgs.state;
            validArgCount += 1;
        }
    }
    await populateForecastPointsForState();

    if (queryArgs["triplet"]) {
        if ([...pointSelect.options].map((o) => o.value).includes(queryArgs.triplet.toUpperCase())) {
            pointSelect.value = queryArgs.triplet;
            validArgCount += 1;
        }
    }
    await populatePeriodsForPoint();

    if (queryArgs["period"]) {
        if ([...periodSelect.options].map((o) => o.value).includes(queryArgs.period.toUpperCase())) {
            periodSelect.value = queryArgs.period;
            validArgCount += 1;
        }
    }
    await populateYearsForPeriod();

    if (queryArgs["year"]) {
        if ([...yearSelect.options].map((o) => o.value).includes(queryArgs.year.toUpperCase())) {
            yearSelect.value = queryArgs.year;
            validArgCount += 1;
        }
    }

    // Add Event Listeners
    addEventListeners();

    // If there are enough URL arguments, create the plot
    if (validArgCount >= 4) {
        try {
            await createEvolutionPlot();
        } catch (err) {
            console.log(err);
            plotting.createEmptyPlot("Could not create a plot for the selected options");
        }
    } else {
        // Initialize an empty plot
        plotting.createEmptyPlot();
    }

    // Finish page load
    loadMainNwcc();
}

/**
 * Returns the DOM form elements from the page
 * @returns
 */
export function getFormObjects() {
    // Get the DOM objects for the selection form
    return {
        stateSelect: document.getElementById("state-select"),
        pointSelect: document.getElementById("forecast-point-select"),
        periodSelect: document.getElementById("forecast-period-select"),
        yearSelect: document.getElementById("forecast-year-select"),
    };
}

/**
 * Adds click/change event listeners to the DOM elements
 */
export function addEventListeners() {
    const { stateSelect, pointSelect, periodSelect } = getFormObjects();
    const loadButton = document.getElementById("forecast-load-button");

    stateSelect.onchange = async () => {
        loadButton.disabled = true;
        await populateForecastPointsForState();
        await populatePeriodsForPoint();
        await populateYearsForPeriod();
        loadButton.disabled = false;
    };

    pointSelect.onchange = async () => {
        loadButton.disabled = true;
        await populatePeriodsForPoint();
        await populateYearsForPeriod();
        loadButton.disabled = false;
    };
    periodSelect.onchange = async () => {
        loadButton.disabled = true;
        await populateYearsForPeriod();
        loadButton.disabled = false;
    };

    loadButton.onclick = async () => {
        updateUrlQueryString();

        const main = document.getElementById("main-content");
        main.style.visibility = "hidden";
        const shadowDom = document.getElementsByTagName("nwcc-header")[0].shadowRoot;
        const splash = shadowDom.getElementById("loading-splash");
        splash.style.display = "flex";

        try {
            await createEvolutionPlot();
        } catch {
            plotting.createEmptyPlot("Could not create a plot for the selected options");
        }

        splash.style.display = "none";
        main.style.visibility = "visible";
    };
    document.getElementById("copy-link-button").onclick = copyLinkToClipboard;
}

/**
 * Creates a static link to the plot and copies that link into the clipboard.
 */
export function copyLinkToClipboard() {
    const { stateSelect, pointSelect, periodSelect, yearSelect } = getFormObjects();

    const state = stateSelect.value;
    const triplet = pointSelect.value;
    const period = periodSelect.value;
    const year = yearSelect.value;

    let textToCopy = window.location.origin + window.location.pathname + "?";
    textToCopy += `state=${state}&triplet=${triplet}&period=${period}&year=${year}`;

    const queryArgs = getQueryArgs();
    if (queryArgs["internal"]) {
        textToCopy += `&forecastStatus=${
            queryArgs["forecastStatus"] === undefined ? "final" : queryArgs["forecastStatus"]
        }&internal=true`;
    }

    navigator.clipboard.writeText(textToCopy).then(
        function () {
            renderGrowl(
                "Copied to clipboard!",
                "Copied the unique URL for this page to the clipboard.<br><pre>" + textToCopy + "</pre>",
                "success",
            );
        },
        function (err) {
            renderGrowl("Error", "There was a error copying the URL to the clipboard.", "error");
        },
    );
}

/**
 * Updates the search parameters in the URL with the values that reflect the current
 * plot selections.
 */
export function updateUrlQueryString() {
    const { stateSelect, pointSelect, periodSelect, yearSelect } = getFormObjects();

    let url = new URL(window.location);
    url.searchParams.set("state", stateSelect.value);
    url.searchParams.set("triplet", pointSelect.value);
    url.searchParams.set("period", periodSelect.value);
    url.searchParams.set("year", yearSelect.value);
    history.pushState(null, "", url.toString());
}

/**
 *
 * Returns a list of forecast statuses from the URL. By default, returns 'final'
 */
export function getStatusList() {
    const queryArgs = getQueryArgs();
    if (queryArgs["forecastStatus"]) {
        let statusString = queryArgs["forecastStatus"];
        if (statusString.toLowerCase() === "all") {
            return ["raw", "prelim", "final", "guidance"];
        }
        let statusList = statusString.split(",").map((s) => s.trim());
        statusList = statusList.filter((s) => ["raw", "prelim", "final", "guidance"].includes(s));

        if (statusList.length === 1 && statusList[0] === "guidance") {
            statusList = ["guidance", "final"];
        }

        return statusList;
    } else {
        return ["final"];
    }
}

/**
 * Reads the current drop-down selections and creates an evolution plot
 * from those selections. This function retrieves the observations,
 * forecasts, and statistics, and sends all the relevent data
 * to the plotting module for plotting.
 */
export async function createEvolutionPlot() {
    const { pointSelect, periodSelect, yearSelect } = getFormObjects();

    // Get selections
    const selectedTriplet = pointSelect.value;
    const selectedPeriod = periodSelect.value;
    const selectedYear = +yearSelect.value;

    // Get status
    const statusList = getStatusList();

    // Get statistics
    let stats = await data.getObservedStats(selectedTriplet, selectedPeriod, selectedYear);

    // Get observations
    let { observed, resolution } = await getObservedData(selectedTriplet, selectedPeriod, selectedYear);

    // accumulate observations
    if (observed) {
        observed = data.accumulateObservations(observed, resolution);
    } else {
        observed = {};
    }

    // Get forecasts
    let forecasts = await data.getForecasts(selectedTriplet, selectedPeriod, selectedYear, statusList);

    // Add observations
    forecasts = data.addPartialObserved(forecasts, observed, selectedPeriod);

    // forecast traces
    let pubDates = forecasts.map((f) => f.publicationDate);

    let guidForecasts = [];
    let nonGuidForecasts = [];

    pubDates = [...new Set(pubDates)].sort((a, b) => a.localeCompare(b));

    for (const pubDate of pubDates) {
        // Get the forecast for pub date
        let fcstsForPubDate = forecasts.filter((f) => f.publicationDate == pubDate);

        // Split out any guidance forecasts from normal forecasts
        let pubDateGuidanceForecasts = fcstsForPubDate.filter((f) => f.forecastStatus === "guidance");
        let pubDateNonGuidanceForecasts = fcstsForPubDate.filter((f) => f.forecastStatus !== "guidance");

        // For the guidance forecasts, simply choose the best residual period
        if (pubDateGuidanceForecasts.length > 0) {
            guidForecasts.push(
                pubDateGuidanceForecasts.toSorted((a, b) => b.forecastPeriod[0].localeCompare(a.forecastPeriod[0]))[0],
            );
        }

        // For the normal forecasts, choose the best status and then choose the best residual period.
        if (pubDateNonGuidanceForecasts.length > 0) {
            let bestForecast = null;
            for (const status of ["raw", "prelim", "final"]) {
                let forecasts = pubDateNonGuidanceForecasts.filter((f) => f.forecastStatus === status);
                if (forecasts.length > 0) {
                    bestForecast = forecasts.toSorted((a, b) =>
                        b.forecastPeriod[0].localeCompare(a.forecastPeriod[0]),
                    )[0];
                }
            }
            if (bestForecast) {
                nonGuidForecasts.push(bestForecast);
            }
        }
    }

    plotting.createEvolutionPlot(
        observed,
        nonGuidForecasts,
        stats,
        selectedPeriod,
        pointSelect.options[pointSelect.selectedIndex].innerHTML,
        guidForecasts,
    );
}

/**
 * Reads the selected State and uses the station data file
 * to populate a list of available stations organized
 * by basin for that state
 */
export async function populateForecastPointsForState() {
    // Get relevant HTML nodes
    const { stateSelect, pointSelect } = getFormObjects();

    // Clear out the point selection node
    while (pointSelect.firstChild) {
        pointSelect.removeChild(pointSelect.firstChild);
    }

    // Get HUC6 values for state
    const state = stateSelect.value;
    let fcstPoints = await data.getStationDataFile("SRVO");
    let basinAssociations = await data.getBasinFcstDataFile();
    basinAssociations = basinAssociations
        .filter((ba) => {
            if (state.toLowerCase() === "mt") {
                return ba.states.includes(state) || ba.states.includes("ND");
            } else {
                return ba.states.includes(state);
            }
        })
        .sort((a, b) => {
            return +a.id - +b.id;
        });

    // Add basins and forecast points to the drop-down
    basinAssociations.forEach((ba) => {
        // Create an opt-group
        let addedAtLeastOneStation = false;
        let optgroup = document.createElement("optgroup");
        optgroup.label = ba.name;

        // Sort stations in the opt-group by ID
        let stations = ba.station_list;
        stations = stations.sort((stationA, stationB) => {
            return stationA.split(":")[0].localeCompare(stationB.split(":")[0]);
        });

        // Create option for each station.
        stations.forEach((station) => {
            let option = document.createElement("option");
            option.value = station;
            let metadata = fcstPoints.filter((p) => p.stationTriplet === station);
            if (metadata.length === 0) return;
            if (Object.keys(metadata[0]).includes("fcstName")) {
                option.innerHTML = metadata[0].fcstName;
                optgroup.appendChild(option);
                addedAtLeastOneStation = true;
            }
        });

        // Append optgroup if it contains at least one forecast point.
        pointSelect.appendChild(addedAtLeastOneStation ? optgroup : document.createElement("b"));
    });
}

/**
 * Reads the selected station triplet from the stations
 * dropdown and populates a list of available periods for
 * the selected station
 */
export async function populatePeriodsForPoint() {
    // Get relevant HTML nodes
    const { periodSelect, pointSelect } = getFormObjects();

    // Get selected forecast point
    const selectedTriplet = pointSelect.value;

    // Clear out the period select
    while (periodSelect.firstChild) {
        periodSelect.removeChild(periodSelect.firstChild);
    }

    // Get forecast periods
    let { periods, primaryPeriod } = await data.getPeriodsForTriplet(selectedTriplet);

    // Add the primary period
    let option = document.createElement("option");
    option.innerHTML = "Primary Period";
    option.value = primaryPeriod;
    periodSelect.appendChild(option);

    // Add the periods
    periods = periods.sort((a, b) => {
        return utils.sortFcstPeriods(a, b);
    });
    periods.forEach((period) => {
        let option = document.createElement("option");
        option.innerHTML = period;
        option.value = period;
        periodSelect.appendChild(option);
    });
}

/**
 * Reads the triplet and selected period and queries the
 * AWDB Rest Service for a list of available years that
 * have forecasts for the selections and the provided forecast
 * statuses.
 */
export async function populateYearsForPeriod() {
    // Get relevant HTML nodes
    const { periodSelect, yearSelect, pointSelect } = getFormObjects();

    // Get triplet and period
    const selectedTriplet = pointSelect.value;
    const selectedPeriod = periodSelect.value;

    // Clear out the year list
    while (yearSelect.firstChild) {
        yearSelect.removeChild(yearSelect.firstChild);
    }
    yearSelect.enabled = false;
    const blankOption = document.createElement("option");
    blankOption.innerHTML = "Loading...";
    yearSelect.prepend(blankOption);

    // Get the years
    let years = await data.getYearsForPeriodAndTriplet(selectedTriplet, selectedPeriod, getStatusList());

    // Add to the year select
    yearSelect.removeChild(yearSelect.firstChild);
    years.forEach((yr) => {
        let option = document.createElement("option");
        option.innerHTML = yr;
        option.value = yr;
        yearSelect.appendChild(option);
    });
    yearSelect.enabled = true;
}

/**
 * Checks a dataset for missing data.
 * @returns
 */
export function checkDatasetForValidity(dataset) {
    // Check if there is any NaN or Null data in the dataset
    if (dataset) {
        if (Object.keys(dataset).length > 0) {
            // Trim the data to the current date if neccessary
            let datasetArray = Object.entries(dataset);
            const filtered = datasetArray.filter(([key, value]) => key < new Date().toISOString());
            dataset = Object.fromEntries(filtered);

            if (
                Object.keys(dataset)
                    .map((k) => dataset[k])
                    .map((v) => v == null || isNaN(v))
                    .some((b) => b)
            ) {
                dataset = null;
            }
        }
    }
    return dataset;
}

/**
 * Iterates through the DAILY/SEMIMONTHLY/MONTHYL data for the
 * selected triplet and period and returns the most resolute
 * observations available.
 */
export async function getObservedData(triplet, period, year) {
    let obsData = null;
    let resolution = null;

    // try to get daily data
    const SAC_IDs = window.tblData.map((eqn) => {
        return eqn.ntwk_sta_id;
    });
    let ntwkId = triplet.split(":")[0];
    if (SAC_IDs.includes(ntwkId)) {
        let eqn = window.tblData[SAC_IDs.indexOf(ntwkId)];
        obsData = await data.getPartialObservedRest(triplet, period, year, "SRVO", eqn);

        // Check if there is any NaN or Null data in the dataset
        obsData = checkDatasetForValidity(obsData);
        resolution = "daily";
    }

    // If there's no daily data, try to get semi-monthly data
    if (!obsData) {
        obsData = await data.getPartialObservedJSON(triplet, period, year, true);
        obsData = checkDatasetForValidity(obsData);
        resolution = "semimonthly";
    }

    // if there's no semi-monthly data, try to get monthly data
    if (!obsData) {
        obsData = await data.getPartialObservedJSON(triplet, period, year);
        obsData = checkDatasetForValidity(obsData);
        resolution = "monthly";
    }

    return { observed: obsData, resolution: resolution };
}

window.onload = pageLoad;
